import { RegisteredUser } from "@robotical/appv2-warranty-service-lib/dist/types/serviceProgramDatabase";
import Logger from "../../services/logger/Logger";
import { getOnboardingCookie, isOnboardingCookieExpired } from "./cookies";
import WarranyFacade from "@robotical/appv2-warranty-service-lib";

const SHOW_LOGS = true;
const TAG = "WarrantyServiceUtils";

export async function shouldShowOnboardingModal(serialNo: string | undefined): Promise<boolean> {
    Logger.info(SHOW_LOGS, TAG, "Checking if should show onboarding modal for serial number: " + serialNo);
    let shouldShowModal = false;
    try {
        if (serialNo) {
            const cookie = getOnboardingCookie();

            const isCookieExpired = isOnboardingCookieExpired();
            const serialNoInCookie = cookie ? cookie.serialNumbers.includes(serialNo) : false;

            const isSerialNoRegistered = await WarranyFacade.isSerialNumberRegistered(serialNo);
            shouldShowModal = (!cookie || isCookieExpired || !serialNoInCookie) && !isSerialNoRegistered;
        }
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error checking if should show onboarding modal: " + error);
        // Don't show the modal if there was an error (probably no internet connection)
        return false;
    }
    return shouldShowModal;
}

export async function getSerialNumberStubbornly(
    serialNumberGetter: () => string | undefined,
    attempts: number
): Promise<string | undefined> {
    Logger.info(SHOW_LOGS, TAG, "Getting serial number stubbornly");
    return new Promise(async (resolve, reject) => {
        let currentAttempt = 1;

        const tryGetSerialNumber = () => {
            let serialNo = serialNumberGetter();
            if (currentAttempt > attempts) {
                reject(new Error('Failed to get serial number after maximum attempts'));
                return;
            }

            if (serialNo) {
                resolve(serialNo);
            } else {
                currentAttempt++;
                setTimeout(tryGetSerialNumber, 1000);
            }
        };

        tryGetSerialNumber();
    });
}


export async function registerUser(email: string, establishment: string, serialNumbers: string[]): Promise<boolean> {
    Logger.info(SHOW_LOGS, TAG, "Registering user with email: " + email);
    try {
        return WarranyFacade.registerUser(email, establishment, serialNumbers);
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error registering user: " + error);
        return false;
    }
}

export async function isUserRegistered(email: string): Promise<boolean> {
    Logger.info(SHOW_LOGS, TAG, "Checking if user is registered with email: " + email);
    try {
        return !!await WarranyFacade.getUser(email);
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error checking if user is registered: " + error);
        return false;
    }
}

export async function getUserGivenEmail(email: string): Promise<RegisteredUser | null> {
    Logger.info(SHOW_LOGS, TAG, "Getting user given email: " + email);
    try {
        const user = await WarranyFacade.getUser(email);
        return user;
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error getting user given email: " + error);
        return null;
    }
}

export const isSerialNumberRegistered = async (serialNo: string): Promise<boolean> => {
    Logger.info(SHOW_LOGS, TAG, "Checking if serial number is registered: " + serialNo);
    try {
       return WarranyFacade.isSerialNumberRegistered(serialNo);
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error checking if serial number is registered: " + error);
        return false;
    }
};

export const createTicket = async (serialNo: string, title: string, description: string, robotName?: string, robotType?: string): Promise<boolean> => {
    Logger.info(SHOW_LOGS, TAG, "Creating ticket for serial number: " + serialNo);
    try {
        const serialNumber = await WarranyFacade.getSerialNumber(serialNo);
        if (serialNumber) {
            return !!await WarranyFacade.registerReport(serialNumber.email, serialNo, title, description, robotName, robotType);
        }
        return false;
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error creating ticket: " + error);
        return false;
    }
};

export const getEmailGivenSerialNumber = async (serialNo: string): Promise<string | undefined> => {
    Logger.info(SHOW_LOGS, TAG, "Getting email given serial number: " + serialNo);
    try {
        const serialNumber = await WarranyFacade.getSerialNumber(serialNo);
        return serialNumber?.email;
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error getting email given serial number: " + error); 
        return undefined;
    }
}

export const addRobotNameToSerialNumber = async (serialNo: string, name: string): Promise<boolean> => {
    Logger.info(SHOW_LOGS, TAG, "Adding robot name to serial number: " + serialNo);
    try {
        return WarranyFacade.addRobotNameToSerialNumber(serialNo, name);
    } catch (error) {
        Logger.error(SHOW_LOGS, TAG, "Error adding robot name to serial number: " + error);
        return false;
    }
}